import { observer } from "mobx-react-lite";

import { NodeValueSuggestionsField } from "@/components/doc/right-sidebar/active-node-style/fields/NodeValueSuggestionsField";
import { setNodeValue } from "@/components/doc/right-sidebar/node-viewer/utils";
import { getSuggestionNode } from "@/components/suggestions/providers/utils";

import type { ValueSuggestionsProps } from "@/components/doc/right-sidebar/active-node-style/fields/NodeValueSuggestionsField";
import type { SingleSuggestion } from "@/components/suggestions/types";
import type { ConstantNodeModel } from "@/models/nodes/ConstantNodeModel";
import type { VariableNodeModel } from "@/models/nodes/VariableNodeModel";

type Props = {
  node: VariableNodeModel | ConstantNodeModel;
  className?: string;
};

export const SymbolValueField = observer(function SymbolValueField({ node, className }: Props) {
  const { doc, symbolNode } = node;

  if (!symbolNode) {
    return null;
  }

  const handleSuggestionSelect = (suggestion: SingleSuggestion) => {
    doc.transact(() => {
      const [newNode] = getSuggestionNode(suggestion, doc) ?? [];

      if (newNode) {
        node.replaceWith(newNode);
      }
    }, "SymbolValueField:suggestionSelect");
    doc.focus();
  };

  const suggestionsProps: ValueSuggestionsProps = {
    initialIndex: -1,
    onSelect: handleSuggestionSelect,
    accept: ["variable", "constant"],
    providerSearchOptions: {
      variable: {
        skipNewNode: true,
        filterExistingNodes: (base, type, ids) => type === "variable" && !ids.has(node.id),
      },
      constant: {
        skipNewNode: true,
        filterExistingNodes: (base, type, ids) => type === "constant" && !ids.has(node.id),
      },
      library: {
        filterExistingNodes: (base, type, ids) => !ids.has(node.id),
      },
    },
  };

  return (
    <NodeValueSuggestionsField
      doc={doc}
      inputProps={{
        id: `value-${symbolNode.id}`,
        value: symbolNode.value,
        className,
      }}
      onSubmit={(value) => setNodeValue(node, value)}
      suggestionsProps={suggestionsProps}
    />
  );
});
